import { GitHubAppInstallQueryField } from '@common/neo.tax';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useUrlQueryParam } from './useUrlQueryParam';

type UseGitHubAppInstallReturnResult = {
  /** The status GitHub's callback sent us back with, or null on a normal page load. */
  installStatus: string | null;
  /** True while the connect modal should be open because of that return. */
  isConnectModalOpen: boolean;
  openConnectModal: () => void;
  /** Closes the modal and strips the status param so a refresh doesn't re-open it. */
  closeConnectModal: () => void;
};

/**
 * Handles a browser coming back from the GitHub App install (PLAT-386).
 *
 * The install is a full-page redirect away and back, and `returnUrl` returns the user to whichever
 * page they started from — so every surface that mounts ConnectProvidersModal needs this, not just
 * the get-started page. A page that ignores the param gives the user no feedback that anything
 * happened and, worse, leaves the param in the URL: ConnectGitHub reads its presence as "returning
 * from an install" and silently skips the method-choice step the next time the modal opens there.
 */
export const useGitHubAppInstallReturn = (): UseGitHubAppInstallReturnResult => {
  const [searchParams, setSearchParams] = useSearchParams();
  const installStatus = useUrlQueryParam(GitHubAppInstallQueryField.CONNECTED_STATUS);
  const [isConnectModalOpen, setIsConnectModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (installStatus) {
      setIsConnectModalOpen(true);
    }
  }, [installStatus]);

  // Stable identity: callers memoize on it, and their memos are what keep a table's column
  // definitions from being rebuilt on every render of a page that polls.
  const closeConnectModal = useCallback((): void => {
    setIsConnectModalOpen(false);
    // Use the router API rather than raw history.replaceState so react-router's history state
    // stays intact.
    if (installStatus) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete(GitHubAppInstallQueryField.CONNECTED_STATUS);
      setSearchParams(nextParams, { replace: true });
    }
  }, [installStatus, searchParams, setSearchParams]);

  return {
    installStatus,
    isConnectModalOpen,
    openConnectModal: () => setIsConnectModalOpen(true),
    closeConnectModal,
  };
};
